// Module 10 — the footprint-fill applier (Batch C-3). The hollow luminous WALL bands (walls.ts) read
// at an oblique angle but VANISH from straight overhead — a thin vertical sheet has ~zero top-down
// footprint. This layer draws the matching FILLED floor: one flat, translucent (≈50% alpha) polygon
// per parcel, from the SAME SceneSpec.walls the wall layer consumes, so a vertical (top-down) view
// shows — and a click selects — every neighbourhood. Same hue as the wall (wallStyle), pinned to 50%
// alpha so the basemap shows through. Geometry is STATIC (the ring never changes); per-tick service
// status only mutates the per-instance COLOUR in place (same discipline as walls.ts — no geometry
// rebuild, no FPS cliff). Boundary: same-module + domain only.
import type { WallSpec } from "./effects/spec";
import { wallStyle } from "./walls";
import type { CesiumLike, SceneLike, PrimitiveLike, GeometryInstanceLike, ColorLike } from "./cesium-api";

// The user's "transparent layer of the same color as the wall ... for example 50%": a fixed fill
// alpha so the parcel floor is visible top-down yet the map/3D-tiles read through it. Independent of
// wallStyle's per-status alpha (which tunes the vertical band's glow) — the floor is always ~half-lit.
export const FOOTPRINT_ALPHA = 0.5;

export interface FootprintLayer {
  apply(walls: readonly WallSpec[]): void;
  setShow(visible: boolean): void; // perf lever (primitive.show) — hidden on the LOW quality tier
  setModelMatrix(m: unknown): void; // 3D "float" lift — Matrix4 applied to the whole primitive
}

export function makeFootprintLayer(cesium: CesiumLike, scene: SceneLike): FootprintLayer {
  let primitive: PrimitiveLike | null = null;
  let liftMatrix: unknown = null; // pending 3D lift if set before the primitive is built
  // identity-stable pickable ids — clicking the parcel floor selects its load node (same id the wall
  // band uses, so an oblique click on the wall and a top-down click on the floor select the same node).
  const idOf = new Map<string, { kind: "node"; id: string }>();
  // hue from wallStyle (kind + service status). The floor carries the per-status alpha directly (no pin)
  // so the parcel reads transparently enough to see the basemap through it, while outage colors
  // (shed vermillion / dark near-black) are still distinct. Clamped to a 0.12 visible+clickable floor
  // (lowered from 0.2 alongside wallStyle's alphas — the floor is the top-down-dominant layer, so it
  // needs to be the MOST transparent of the two to fix the "solid mosaic hides the basemap" complaint).
  const colorOf = (w: WallSpec): ColorLike => {
    const s = wallStyle(w);
    const alpha = Math.max(0.12, s.alpha * 0.7); // scale down for the floor (it's top-down dominant)
    return cesium.Color.fromCssColorString(s.hex).withAlpha(alpha);
  };

  return {
    apply(walls): void {
      if (walls.length === 0) return; // no parcels → nothing to draw (not an error)
      if (!primitive) {
        const instances: GeometryInstanceLike[] = [];
        for (const w of walls) {
          if (w.ring.length < 3) continue; // a filled polygon needs ≥3 vertices (a band can be 2)
          const flat: number[] = [];
          for (const c of w.ring) flat.push(c.lon, c.lat);
          const geometry = cesium.PolygonGeometry.fromPositions({
            positions: cesium.Cartesian3.fromDegreesArray(flat),
            height: 0, // flat on the ellipsoid — a top-down-visible floor beneath the hollow wall band
          });
          const pickId = { kind: "node" as const, id: w.id };
          idOf.set(w.id, pickId);
          instances.push(new cesium.GeometryInstance({
            geometry,
            id: pickId, // pickable + identity-stable for the in-place recolour lookup
            attributes: { color: cesium.ColorGeometryInstanceAttribute.fromColor(colorOf(w)) },
          }));
        }
        if (instances.length === 0) return; // every parcel was degenerate (<3 verts) — no primitive
        primitive = new cesium.Primitive({
          geometryInstances: instances,
          // flat:true reads cleanly top-down (no shading gradient); translucent:true blends the 50% alpha.
          appearance: new cesium.PerInstanceColorAppearance({ flat: true, translucent: true }),
          asynchronous: true, // off-thread build, same as the neighbourhood walls (no main-thread freeze)
        });
        if (liftMatrix != null) primitive.modelMatrix = liftMatrix; // apply a lift set before build
        scene.primitives.add(primitive);
        return;
      }
      // already built — recolour in place from the new per-tick status (no geometry rebuild). Skip
      // until the primitive has rendered once (getGeometryInstanceAttributes throws before the first
      // update pass — the same hidden-tab/rAF-pause guard as walls.ts).
      if (!primitive.ready) return;
      for (const w of walls) {
        const pickId = idOf.get(w.id);
        if (!pickId) continue;
        const attr = primitive.getGeometryInstanceAttributes(pickId);
        attr.color = cesium.ColorGeometryInstanceAttribute.toValue(colorOf(w), attr.color); // toValue(color, result)
      }
    },
    setShow(visible): void {
      if (primitive) primitive.show = visible;
    },
    setModelMatrix(m): void {
      liftMatrix = m;
      if (primitive) primitive.modelMatrix = m;
    },
  };
}
